import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import Swiper from 'react-id-swiper';
import parse from 'html-react-parser';
import Heading from 'js/components/Common/Heading/Heading';

import Loader from 'js/components/Common/Loader/Loader';
import useFetchData from 'js/hooks/useFetchData/useFetchData';
import Container from 'js/components/Common/Container/Container';
import DataValidator from 'js/components/Common/DataValidator/DataValidator';
import HomeSliderService from 'js/components/Common/HomeSliderService/HomeSliderService';
import EntitySliderCard, {EntityType} from 'js/components/Common/HomeSliderService/EntitySliderCard/EntitySliderCard';

import fetchData, {tabsData} from './fetchData';
import ExploreContentTabs from './ExploreContentTabs';
import classes from './ExploreContentSlider.module.pcss';
import MixPanelWrapper from 'js/servises/MixPanelWrapper';

interface Props {
    pageId: number
}

export type TabType = {
    name: string,
    id: string,
    grId: string
}

const ExploreContentSlider = ({pageId}: Props) => {
    const nodeRef = useRef(null);
    const [slider, setSlider] = useState(null);
    const [title, setTitle] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [tabs, setTabs] = useState<Array<TabType>>(null);
    const [contentItems, setContentItems] = useState<Array<EntityType>>(null);
    const [activeTab, setActiveTab] = useState<string>(null);
    const [wrapOffset, setWrapOffset] = useState<string>('');

    const {isLoading, error} = useFetchData({
        query: `${fetchData(pageId, activeTab)}`,
        condition: !!pageId,
        updateData: (data: any): void => {
            if (slider) {
                slider.destroy(true, true);
            }
            const pageResult = data?.page?.PostLoginPage;
            setTitle(pageResult?.exploreSliderTitle);
            setDescription(pageResult?.exploreSliderDescription);
            setContentItems(data?.exploreContentByInterests);
            if (slider && data?.exploreContentByInterests?.length > 0) {
                slider.init();
            }
        }
    });

    useFetchData({
        query: `${tabsData}`,
        condition: true,
        updateData: (data: any): void => {
            const result = data?.exploreProductList;
            setTabs(data?.exploreProductList);
            if (result?.length > 0) {
                tabClickHandler(data?.exploreProductList[0]?.id, data?.exploreProductList[0]?.name, true);
            }
        }
    });

    const sendMixPanelButtonEvent = useCallback(async (buttonName: string) => {
        await MixPanelWrapper.TrackEvent(
            'Filter button is clicked',
            {
                category: 'Home page',
                section: 'Explore section',
                value: buttonName
            });
    }, []);

    const tabClickHandler = useCallback(async (id: string, name: string, isDefault: boolean = false) => {
        setActiveTab((prevState) => {
            if (parseInt(prevState) !== parseInt(id)) {
                setContentItems(null);
                return id;
            }
            return id;
        });
        if (!isDefault) {
            await sendMixPanelButtonEvent(name);
        }
    }, [sendMixPanelButtonEvent]);

    const params = useMemo(() =>
        HomeSliderService.getParams(setSlider, classes.Slider, classes.SecondWrap, classes.Bullets, false, false), []);

    useEffect(() => {
        if (!slider) {
            return;
        }
        const paginationBullets = slider?.pagination?.bullets;
        if (!paginationBullets) {
            return;
        }
        HomeSliderService.setSliderBulletsClickEvent(
            paginationBullets,
            tabs?.find(item => item?.id === activeTab)?.name,
            'Explore section');
    }, [activeTab, slider, tabs]);

    const updateMarginLeft = useCallback(() => {
        const parent:any = nodeRef?.current;
        if (!parent) return;
        const exploreWrap = parent?.childNodes[0];
        if (!exploreWrap) return null;

        const computedStyle = window.getComputedStyle(exploreWrap);

        setWrapOffset(computedStyle?.marginLeft);
    }, []);

    useEffect(() => {
        updateMarginLeft();
    }, [updateMarginLeft, tabs]);

    useEffect(() => {
        window.addEventListener('resize', updateMarginLeft);

        return () => {
            window.removeEventListener('resize', updateMarginLeft);
        };
    }, [updateMarginLeft]);

    return (
        <DataValidator data={tabs?.length > 0}>
            <section ref={nodeRef} className={classes.ExploreContentSlider}>
                <Container additionalClass={`${classes.TopWrap} .ExploreWrap`}>
                    <Heading text={title}/>
                    <p className={classes.Description}>{parse(description || '')}</p>
                    <ExploreContentTabs
                        tabs={tabs}
                        activeTab={activeTab}
                        tabClickHandler={(id, name) => tabClickHandler(id, name)}
                    />
                </Container>
                {!contentItems?.length ? <Loader/> : ''}
                <DataValidator data={!isLoading && !error && contentItems?.length > 0}>
                    <div className={classes.Slider}>
                        <Container additionalClass={classes.Wrap} additionalStyles={{
                            marginLeft: wrapOffset
                        }}>
                            <Swiper {...params}>
                                {contentItems?.map(slide => (
                                    <div
                                        className={`${classes.ExploreContentSliderItem} swiper-slide`}
                                        key={slide?.id}>
                                        <EntitySliderCard
                                            data={slide}
                                            mixPanelEventData={{
                                                section: 'Explore section',
                                                category: 'Home page'
                                            }}
                                        />
                                    </div>
                                ))}
                            </Swiper>
                        </Container>
                        <Container additionalClass={classes.SecondWrap}>
                            <div className={classes.Bullets}/>
                        </Container>
                    </div>
                </DataValidator>
            </section>
        </DataValidator>
    );
};

export default ExploreContentSlider;
